import React, { FC, useContext, useEffect, useState } from 'react';
import { FlightFilterModel } from 'ts/dynamic/classes/DynamicFilterFlight';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { getDurationFromTime } from 'utils/utils';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import FlightResultFilterSlider from './DynamicFilterSlider';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { DynamicFlight } from 'ts/dynamic/interfaces/DynamicResponse';
import { DynamicContext } from 'contexts/DynamicContext';
import { formatPriceDynamic } from 'utils/dynamic/dynamic';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface DynamicFlightFiltersProps {
  flights: DynamicFlight[];
  onFiltering: (filter: FlightFilterModel) => void;
}

const DynamicFlightFilters: FC<DynamicFlightFiltersProps> = ({ flights, onFiltering }) => {
  const [t] = useTranslation();
  const { currency } = useContext(DynamicContext);
  const [filter, setFilter] = useState(new FlightFilterModel(flights, currency));
  const onClickCheckbox = (
    value: boolean,
    index: number,
    option: 'stops' | 'airlines' | 'airports' | 'flightTypes' | 'baggage'
  ) => {
    const newStops = [...filter[option]];
    newStops[index].checked = value;
    onChange({ ...filter, [option]: newStops });
  };

  useEffect(() => {
    setFilter(new FlightFilterModel(flights, currency));
  }, [flights.length]); // eslint-disable-line

  const onClickReset = () => {
    setFilter(new FlightFilterModel(flights, currency));
    onFiltering(new FlightFilterModel(flights, currency));
  };

  const label = (entity: any, tr?: boolean) => {
    return (
      <span className="flex-between w100p text-lh-sm">{tr ? t(entity.label) : entity.label}</span>
    );
  };

  const showClearFilter = () => {
    const depTimesChanged =
      filter.departureOutboundTime &&
      filter.departureOutboundTime &&
      filter.arrivalOutboundTime &&
      filter.arrivalOutboundTime &&
      (filter.departureOutboundTime.value[0] !== filter.departureOutboundTime.min ||
        filter.departureOutboundTime.value[1] !== filter.departureOutboundTime.max ||
        filter.arrivalOutboundTime.value[0] !== filter.arrivalOutboundTime.min ||
        filter.arrivalOutboundTime.value[1] !== filter.arrivalOutboundTime.max);

    const arrTimesChanged =
      filter.departureInboundTime &&
      (filter.departureInboundTime.value[0] !== filter.departureInboundTime.min ||
        filter.departureInboundTime.value[1] !== filter.departureInboundTime.max) &&
      filter.arrivalInboundTime &&
      (filter.arrivalInboundTime.value[0] !== filter.arrivalInboundTime.min ||
        filter.arrivalInboundTime.value[1] !== filter.arrivalInboundTime.max);
    return (
      filter.stops.some((stop) => stop.checked) ||
      filter.airlines.some((airline) => airline.checked) ||
      filter.airports.some((airport) => airport.checked) ||
      filter.flightTypes.some((flightType) => flightType.checked) ||
      filter.price.value[0] !== filter.price.min ||
      filter.price.value[1] !== filter.price.max ||
      filter.duration.value[0] !== filter.duration.min ||
      filter.duration.value[1] !== filter.duration.max ||
      filter.stopDuration.value[0] !== filter.stopDuration.min ||
      filter.stopDuration.value[1] !== filter.stopDuration.max ||
      depTimesChanged ||
      arrTimesChanged
    );
  };

  const onChange = (newFilter: FlightFilterModel) => {
    setFilter(newFilter);
    onFiltering(newFilter);
  };

  const minStopStr = getDurationFromTime(filter.stopDuration.value[0]);

  return (
    <div onClick={onClickOverlay}>
      <div className="flight-filter">
        <div className="results-responsive-header results-responsive-header-flight">
          <span className="title text-md">Filter</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        <section className="flight-filter-section">
          <div className="flex-between mb-15">
            <div className="text-bold">{t('filter.stops')}</div>
            {showClearFilter() && (
              <div className="text-underline text-sm" onClick={onClickReset}>
                {t('filter.reset')}
              </div>
            )}
          </div>

          {filter.stops.map((stop, index) => (
            <Checkbox
              key={stop.number}
              checked={stop.checked}
              htmlLabel={label(stop, true)}
              onChange={(value) => onClickCheckbox(value, index, 'stops')}
              className="mb-10 text-sm"
              disabled={stop.disabled}
            />
          ))}
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.baggage')}</div>
          {filter.baggage.map((baggage, index) => (
            <Checkbox
              key={baggage.number}
              htmlLabel={label(baggage, true)}
              checked={baggage.checked}
              onChange={(value) => onClickCheckbox(value, index, 'baggage')}
              className="mb-10 text-sm"
              disabled={baggage.disabled}
            />
          ))}
        </section>
        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.price_range')}</div>
          <FlightResultFilterSlider
            filter={filter}
            onChange={onChange}
            minStr={formatPriceDynamic(filter.price.value[0], filter.price.currencySign)}
            maxStr={formatPriceDynamic(filter.price.value[1], filter.price.currencySign)}
            type="price"
          />
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.duration')}</div>
          <FlightResultFilterSlider
            filter={filter}
            onChange={onChange}
            minStr={getDurationFromTime(filter.duration.value[0] * 1000 * 60)}
            maxStr={getDurationFromTime(filter.duration.value[1] * 1000 * 60)}
            type="duration"
          />
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.stop_duration')}</div>
          <FlightResultFilterSlider
            filter={filter}
            onChange={onChange}
            minStr={minStopStr === '  ' ? `0 ${t('minutes_sm')}` : minStopStr}
            maxStr={getDurationFromTime(filter.stopDuration.value[1])}
            type="stopDuration"
          />
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.airlines')}</div>
          {filter.airlines.map((airline, index) => (
            <Checkbox
              key={airline.code}
              htmlLabel={label(airline)}
              checked={airline.checked}
              onChange={(value) => onClickCheckbox(value, index, 'airlines')}
              className="mb-10 text-sm"
            />
          ))}
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('filter.airports')}</div>
          {filter.airports.map((airport, index) => (
            <Checkbox
              key={airport.code}
              htmlLabel={label(airport)}
              checked={airport.checked}
              onChange={(value) => onClickCheckbox(value, index, 'airports')}
              className="mb-10 text-sm"
            />
          ))}
        </section>

        <section className="flight-filter-section">
          <div className="text-bold mb-15">{t('flight.s')}</div>
          {filter.flightTypes.map((type, index) => (
            <Checkbox
              key={type.code}
              htmlLabel={label(type, true)}
              checked={type.checked}
              onChange={(value) => onClickCheckbox(value, index, 'flightTypes')}
              className="mb-10 text-sm"
            />
          ))}
        </section>
      </div>
    </div>
  );
};

export default withErrorBoundary(DynamicFlightFilters, 'DynamicFlightFilters');
